import { createInertiaApp, router } from '@inertiajs/react'
import { createElement, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { initSentryFromMeta, syncSentryUser } from '@/lib/sentry'
import { installStandaloneRecovery } from '@/lib/standalone-recovery'
import type { CurrentUser } from '@/types/inertia'

type ResolvedComponent = {
  default: ReactNode
  layout?: (page: ReactNode) => ReactNode
}

type SharedPageProps = {
  current_user?: CurrentUser
}

// Recover iOS Home Screen (standalone PWA) sessions from a dead/suspended
// WebKit context. Client-only — never imported by the SSR entrypoint.
installStandaloneRecovery()

// GlitchTip via Sentry browser SDK — DSN comes from layout meta tags at
// request time (not baked into the Vite bundle).
initSentryFromMeta()

// Keep admin screens and editor dependencies out of public-page downloads.
const pages = import.meta.glob<ResolvedComponent>('../pages/**/*.tsx')

createInertiaApp({
  resolve: async (name) => {
    const loadPage = pages[`../pages/${name}.tsx`]
    if (!loadPage) {
      throw new Error(`Missing Inertia page component: '${name}.tsx'`)
    }
    return loadPage()
  },

  setup({ el, App, props }) {
    const initialUser = (props.initialPage.props as SharedPageProps)
      .current_user
    syncSentryUser(initialUser ?? null)

    router.on('success', (event) => {
      const user = (event.detail.page.props as SharedPageProps).current_user
      syncSentryUser(user ?? null)
    })

    if (el) {
      createRoot(el).render(
        createElement(ErrorBoundary, null, createElement(App, props)),
      )
    } else {
      console.error(
        'Missing root element. Move `vite_typescript_tag "inertia"` into an Inertia-specific layout.',
      )
    }
  },
})
